import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth as supabaseAuth, supabase } from './supabase';
import { userService } from '../services/database';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [processingUser, setProcessingUser] = useState(null); // Track which user is being processed

  // Initialize auth state and listen for changes
  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        const { session } = await supabaseAuth.getSession();
        if (session?.user) {
          await handleUserSession(session.user);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabaseAuth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session?.user?.id);

      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
        // Prevent duplicate processing for the same user
        if (processingUser === session.user.id) {
          console.log('⏸️ Skipping duplicate auth processing for user:', session.user.id);
          return;
        }

        console.log('🔄 Processing auth for:', session.user.email);
        setProcessingUser(session.user.id);
        try {
          await handleUserSession(session.user);
          console.log('✅ Auth processing completed for:', session.user.email);
        } catch (error) {
          console.error('❌ Auth processing error:', error);
        } finally {
          setProcessingUser(null);
        }
      } else if (event === 'SIGNED_OUT') {
        handleSignOut();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleUserSession = async (authUser) => {
    try {
      // Check if Supabase is properly configured
      if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
        console.warn('⚠️ Supabase not configured, using fallback auth');
        // Create a fallback user object
        const userData = {
          id: authUser.id,
          email: authUser.email,
          name: authUser.email.split('@')[0],
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(authUser.email)}&background=random`,
          plan: 'Free',
          planType: 'free',
          metadata: authUser.user_metadata || {}
        };
        setUser(userData);
        setProfile(null);
        return;
      }

      // Get or create user profile
      let { data: userProfile, error } = await userService.getProfile(authUser.id);

      if (error) {
        // Check if it's a "no rows" error (user doesn't exist)
        if (error.message?.includes('Results contain 0 rows') ||
            error.code === 'PGRST116' ||
            error.details?.includes('Results contain 0 rows') ||
            error.message?.includes('Cannot coerce the result to a single JSON object')) {

          console.log('🔨 Creating new user profile for:', authUser.email);

          // User profile doesn't exist, create it
          const profileData = {
            email: authUser.email,
            full_name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || '',
            avatar_url: authUser.user_metadata?.avatar_url || authUser.user_metadata?.picture || ''
          };

          const { data: newProfile, error: createError } = await userService.createProfile(authUser.id, profileData);

          if (createError) {
            // Check if it's a duplicate key error (user was created in another tab/session)
            if (createError.code === '23505') {
              console.log('👥 User profile already exists (created elsewhere), fetching...');
              const { data: existingProfile } = await userService.getProfile(authUser.id);
              userProfile = existingProfile;
            } else {
              console.error('❌ Error creating user profile:', createError);
              // Continue with auth user data even if profile creation fails
              const userData = {
                id: authUser.id,
                email: authUser.email,
                name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || authUser.email.split('@')[0],
                avatar: authUser.user_metadata?.avatar_url || authUser.user_metadata?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(authUser.email)}&background=random`,
                plan: 'Free',
                planType: 'free',
                metadata: authUser.user_metadata || {}
              };
              setUser(userData);
              setProfile(null);
              return;
            }
          } else {
            console.log('✅ User profile created successfully');
            userProfile = newProfile;
          }
        } else {
          console.error('❌ Error fetching user profile:', error);
          // Continue with auth user data even if profile fetch fails
          const userData = {
            id: authUser.id,
            email: authUser.email,
            name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || authUser.email.split('@')[0],
            avatar: authUser.user_metadata?.avatar_url || authUser.user_metadata?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(authUser.email)}&background=random`,
            plan: 'Free',
            planType: 'free',
            metadata: authUser.user_metadata || {}
          };
          setUser(userData);
          setProfile(null);
          return;
        }
      }

      const userData = {
        id: authUser.id,
        email: authUser.email,
        name: userProfile?.full_name || authUser.user_metadata?.full_name || authUser.user_metadata?.name || authUser.email.split('@')[0],
        avatar: userProfile?.avatar_url || authUser.user_metadata?.avatar_url || authUser.user_metadata?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(authUser.email)}&background=random`,
        plan: userProfile?.plans?.name || 'Free',
        planType: userProfile?.plans?.type || 'free',
        metadata: authUser.user_metadata || {}
      };

      setUser(userData);
      setProfile(userProfile);
    } catch (error) {
      console.error('❌ Error handling user session:', error);

      // Fallback to basic auth user data
      const userData = {
        id: authUser.id,
        email: authUser.email,
        name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || authUser.email.split('@')[0],
        avatar: authUser.user_metadata?.avatar_url || authUser.user_metadata?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(authUser.email)}&background=random`,
        plan: 'Free',
        planType: 'free',
        metadata: authUser.user_metadata || {}
      };
      setUser(userData);
      setProfile(null);
    }
  };

  const handleSignOut = () => {
    setUser(null);
    setProfile(null);
    setProcessingUser(null); // Clear processing state
    // Clear any other user-specific data
    localStorage.removeItem('VIBALITY_USER_IDEA');
    localStorage.removeItem('VIBALITY_CONFIRMED_MARKET');
    localStorage.removeItem('VIBALITY_SEGMENTATION_TREES');
    localStorage.removeItem('VIBALITY_SELECTED_SEGMENTS');
    localStorage.removeItem('VIBALITY_CURRENT_STEP');
  };

  const login = async (userData) => {
    if (userData.provider === 'google') {
      const { error } = await supabaseAuth.signInWithGoogle();
      if (error) throw error;
      // User data will be set by the auth state change listener
    } else {
      // Email/password login
      const { error } = await supabaseAuth.signIn(userData.email, userData.password);
      if (error) throw error;
    }
  };

  const register = async (userData) => {
    const { data, error } = await supabaseAuth.signUp(
      userData.email,
      userData.password,
      {
        full_name: userData.name
      }
    );

    if (error) throw error;
    return data;
  };

  const logout = async () => {
    try {
      const { error } = await supabaseAuth.signOut();
      if (error) throw error;
      // handleSignOut will be called by the auth state change listener
    } catch (error) {
      console.error('Error signing out:', error);
      // Force logout on error
      handleSignOut();
    }
  };

  const value = {
    user,
    profile,
    isLoading,
    login,
    register,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};